'use client';
import { useState, useCallback, useEffect, useContext, FC } from 'react';
import { SessionContext } from '@benzinga/session-context';
import { ArticleManager, RecommendedArticle, getCanonicalUrl } from '@benzinga/article-manager';
import { TrackingManager } from '@benzinga/tracking-manager';
import { UserManager } from '@benzinga/user-manager';

import Image from 'next/image';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { Impression } from '@benzinga/analytics';
import { BenzingaLogo } from '@benzinga/logos-ui';
import { formatImageUrl } from '@benzinga/utils';

import styles from './Recommended.module.scss';

export interface RecommendedArticlesProps {
  articleId: number;
  onHasStories?: (hasStories: boolean) => void;
}

interface Recommendation {
  article_data: RecommendedArticle;
  score: number;
}

const RECOMMENDED_LIMIT = 4;

export const RecommendedArticles: FC<RecommendedArticlesProps> = ({ articleId, onHasStories }) => {
  const session = useContext(SessionContext);
  const trackingManager = session.getManager(TrackingManager);
  const userId = session.getManager(UserManager).getUser()?.id?.toString() ?? '';
  const [articles, setArticles] = useState<Recommendation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const trackRecommendedEvent = useCallback(
    recommendedArticles => {
      if (recommendedArticles.length === 0) return;
      trackingManager.trackRecommendedEvent('recommendation_results', {
        recommended_results: recommendedArticles.map((rec, idx) => ({
          article_id: rec.article_data.article_id,
          rank: idx + 1,
          score: rec.score,
        })),
        source_article_id: articleId.toString(),
      });
    },
    [trackingManager, articleId],
  );

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const response = await session
          .getManager(ArticleManager)
          .getRecommendedArticles(articleId, userId, RECOMMENDED_LIMIT);

        if (response.err) {
          console.error('Failed to fetch recommended articles', response.err);
          return;
        }

        const recommendations = response.ok?.recommendations ?? [];
        if (recommendations.length > 0) {
          setArticles(recommendations);
          trackRecommendedEvent(recommendations);
        }
      } catch (error) {
        console.error('Failed to fetch recommended articles', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isLoading) {
      fetchRecommendations();
    }
  }, [articleId, userId, isLoading, session, trackRecommendedEvent]);

  useEffect(() => {
    if (isLoading) return;
    onHasStories?.(articles.length > 0);
  }, [articles.length, isLoading, onHasStories]);

  if (!isLoading && articles.length === 0) {
    return null;
  }

  return (
    <aside
      aria-busy={isLoading || undefined}
      aria-label="Benzinga recommended stories"
      className={styles['recommended-widget']}
      id="recommended-articles-widget"
    >
      <header className={styles['recommended-widget__header']}>
        <BenzingaLogo className="w-full max-w-[120px]" variant="light" />
        <p className={styles['recommended-widget__subtitle']}>Recommended Stories</p>
      </header>

      <ul className={`${styles['recommended-widget__list']} ${isLoading ? ' animate-pulse' : ''}`}>
        {isLoading &&
          Array.from({ length: 4 }).map((_, idx) => (
            <li key={`skeleton-${idx}`}>
              <div aria-hidden className={styles['story']} style={{ pointerEvents: 'none' }}>
                <div className={styles['story__thumb']} style={{ background: 'var(--bzgray-300)' }} />
                <div className={styles['story__text']}>
                  <h3
                    className={styles['story__title']}
                    style={{ background: 'var(--bzgray-300)', borderRadius: 4, color: 'transparent' }}
                  >
                    &nbsp;
                  </h3>
                  <p
                    className={styles['story__abstract']}
                    style={{ background: 'var(--bzgray-300)', borderRadius: 4, color: 'transparent' }}
                  >
                    &nbsp;
                  </p>
                </div>
              </div>
            </li>
          ))}
        {articles.length > 0 &&
          !isLoading &&
          articles.map((rec, idx) => (
            <li key={rec.article_data.article_id}>
              <a
                className={styles['story']}
                data-action="click"
                data-category="Recommended Articles"
                data-recommendation-rank={idx + 1}
                data-recommendation-score={rec.score}
                href={getCanonicalUrl({
                  canonicalPath: rec.article_data.canonical_path ?? '',
                  nodeId: rec.article_data.article_id,
                })}
              >
                {rec.article_data.thumbnail_path ? (
                  <Image
                    alt={rec.article_data.title ?? ''}
                    className={`${styles['story__thumb']} max-w-full object-cover`}
                    height={88}
                    sizes="(max-width: 480px) 72px, 88px"
                    src={formatImageUrl({ image: 'https://cdn.benzinga.com/' + rec.article_data.thumbnail_path })}
                    width={88}
                  />
                ) : (
                  <div className={styles['story__thumb']} style={{ background: 'var(--bzblue-300)' }} />
                )}
                <div className={styles['story__text']}>
                  <h3
                    className={styles['story__title']}
                    dangerouslySetInnerHTML={{ __html: sanitizeHTML(rec.article_data.title ?? '') }}
                  />
                </div>
              </a>
            </li>
          ))}
      </ul>

      <Impression campaign_id="recommended-articles-newsdesk" unit_type="recommended-articles-attribution">
        <footer className={styles['recommended-widget__attribution']}>
          Powered by{' '}
          <a
            href="https://www.benzinga.com/apis/cloud-product/stock-market-news-api/?utm_source=benzinga.com&utm_medium=widget-attribution&utm_campaign=recommended-articles-newsdesk"
            rel="noopener noreferrer"
            target="_blank"
          >
            Benzinga Newsdesk
          </a>
        </footer>
      </Impression>
    </aside>
  );
};
